import { useQuery } from '@tanstack/react-query';
import type {
  PreviewPriorAuthRequestT,
  PreviewPriorAuthResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { fetchPreviewPriorAuth } from '../api/previewPriorAuth.js';
import { type PreviewPriorAuthQueryKeyParams, rxKeys } from './keys.js';

export interface UsePreviewPriorAuthQueryInput {
  userId: string;
  params: PreviewPriorAuthQueryKeyParams;
  enabled?: boolean;
}

/**
 * Pre-submit PA preview. Calls the main backend's
 * ``/dosespot/prescriptions/preview_prior_auth/{user_id}`` endpoint,
 * which in turn runs the real formulary gate against DoseSpot (or the
 * fake DoseSpot in dev). Consumed by the Rx wizard's ``ReviewStep`` to
 * render a soft "Likely prior authorization" banner when the selected
 * medication would trip the gate.
 *
 * The ``params`` object is deliberately narrow — only the fields the
 * gate actually consults are included in the query key. Changing a
 * direction string or an effective date won't trigger a re-fetch.
 *
 * React-query's own debouncing handles the "user is adjusting inputs"
 * case: successive renders with the same key share the in-flight
 * request, and ``placeholderData: (previous) => previous`` keeps the
 * last-known result visible while a new one is being resolved so the
 * banner doesn't flicker between keystrokes.
 */
export function usePreviewPriorAuthQuery({
  userId,
  params,
  enabled = true,
}: UsePreviewPriorAuthQueryInput) {
  const hasMedicationAndPharmacy = params.dispensableDrugId !== null && params.pharmacyId !== null;
  return useQuery<PreviewPriorAuthResponseT>({
    queryKey: rxKeys.previewPriorAuth(userId, params),
    queryFn: ({ signal }) => {
      const request: PreviewPriorAuthRequestT & { patientEligibilityId?: number } = {
        dispensableDrugId: params.dispensableDrugId ?? undefined,
        pharmacyId: params.pharmacyId ?? undefined,
        quantity: params.quantity ?? undefined,
        daysSupply: params.daysSupply ?? undefined,
        dispenseUnitTypeId: params.dispenseUnitTypeId ?? undefined,
        patientEligibilityId: params.patientEligibilityId ?? undefined,
      };
      return fetchPreviewPriorAuth(userId, request, signal);
    },
    enabled: enabled && hasMedicationAndPharmacy,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    meta: {
      toast: {
        title: 'Could not preview prior authorization',
        description:
          'PA status could not be fetched from DoseSpot. You can still send the prescription; final determination happens after signing.',
      },
    },
  });
}
